import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthentication } from '../customHooks/useAuthentication'
import { logoutSession } from '../utils/sessionAuth'
import { fetchMeAccess } from './accessApi'
import { hasAnyPermission, hasPermission, isProfileRequired, normalizeAccess, pickWarehouseId } from './accessPolicy'

export const ACCESS_DENIED_EVENT = 't4-access-denied'
const SELECTED_WAREHOUSE_KEY = 'access_selected_warehouse'

const AccessContext = React.createContext(null)

export function AccessProvider({ children }) {
  const { currentToken, userInfo } = useAuthentication()
  const [access, setAccess] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(() => {
    const raw = sessionStorage.getItem(SELECTED_WAREHOUSE_KEY)
    return raw ? Number(raw) : null
  })
  const refreshLock = useRef(false)

  const load = useCallback(async () => {
    if (!currentToken) {
      setAccess(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const data = await fetchMeAccess()
      const next = normalizeAccess(data)
      setAccess(next)
      setSelectedWarehouseId((current) => {
        const picked = pickWarehouseId(next, current)
        if (picked) sessionStorage.setItem(SELECTED_WAREHOUSE_KEY, String(picked))
        else sessionStorage.removeItem(SELECTED_WAREHOUSE_KEY)
        return picked
      })
    } catch {
      setAccess(normalizeAccess(null, { failed: true }))
    } finally {
      setLoading(false)
    }
  }, [currentToken])

  useEffect(() => {
    load()
  }, [load, userInfo?.id])

  useEffect(() => {
    const onFocus = () => {
      if (refreshLock.current) return
      refreshLock.current = true
      load().finally(() => {
        refreshLock.current = false
      })
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])

  useEffect(() => {
    const onDenied = (event) => {
      const code = event.detail?.code
      if (!code || refreshLock.current) return
      if (!['profile_required', 'warehouse_forbidden'].includes(code)) return
      refreshLock.current = true
      load().finally(() => {
        window.setTimeout(() => {
          refreshLock.current = false
        }, 1500)
      })
    }
    window.addEventListener(ACCESS_DENIED_EVENT, onDenied)
    return () => window.removeEventListener(ACCESS_DENIED_EVENT, onDenied)
  }, [load])

  const chooseWarehouse = useCallback((id) => {
    const next = id ? Number(id) : null
    setSelectedWarehouseId(next)
    if (next) sessionStorage.setItem(SELECTED_WAREHOUSE_KEY, String(next))
    else sessionStorage.removeItem(SELECTED_WAREHOUSE_KEY)
  }, [])

  const value = useMemo(() => {
    const ready = Boolean(access?.ready)
    return {
      access,
      loading,
      ready,
      failed: Boolean(access?.failed),
      profileRequired: isProfileRequired(access),
      selectedWarehouseId,
      chooseWarehouse,
      reload: load,
      logout: () => logoutSession(),
      can: (permission) => hasPermission(access, permission),
      canAny: (permissions) => hasAnyPermission(access, permissions),
    }
  }, [access, loading, selectedWarehouseId, chooseWarehouse, load])

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
}

export function useAccess() {
  const value = useContext(AccessContext)
  if (!value) {
    throw new Error('useAccess requires AccessProvider')
  }
  return value
}
